/**
 * Car domain schemas, derived from the generated Zod.
 *
 * EACH SCHEMA DERIVES FROM ITS OWN ENDPOINT'S BASE — load-bearing, not
 * bookkeeping. The cars app exposes several serializers over the same model and
 * they disagree field-by-field:
 *   - `carSchema`            ← `CarsRetrieveResponse`                 (CarSerializer)
 *   - `carInPropertySchema`  ← `CarsKpisByPropertyIdListResponseItem` (CarInPropertySerializer)
 *   - `violationSchema`      ← `CarsViolationsRetrieveResponse`       (ViolationBasicSerializer)
 *   - `carCertificateSchema` ← `CarsComplianceDocumentsListResponseItem`
 * Building one off a sibling's base is the drift this pattern exists to prevent.
 *
 * `carSchema` HAS NO PRODUCTION CONSUMER. Only the fixture test imports it —
 * `useCar`/`usePropertyCars` parse the generated schemas directly, and
 * `portfolioPropertySchema` (which embeds it) has zero references.
 *
 * The four fields that used to make it throw on a strict parse are gone as of
 * AMP-619: `serviceProviderInternalId` is `allow_null` at source, and
 * `latestVitals`/`lastTripDt`/`lastPingDt` left the API surface entirely when
 * IoT ownership was divested. No override is needed for any of them.
 *
 * See ./README.md.
 */
import { z } from 'zod';

import {
  CarsComplianceDocumentsListResponseItem,
  CarsKpisByPropertyIdListResponseItem,
  CarsRetrieveResponse,
  CarsViolationsRetrieveResponse,
} from '~/data/generated/zod/cars/cars.zod';

import { propertySchema } from './property';

export const carSchema = CarsRetrieveResponse.extend({
  // Kept `.optional()` against the generated base, which types `property`
  // required: the fixture (and the form variant) omit the key.
  property: propertySchema.optional(),
});

export const carDtoSchema = z.object({
  id: z.string().optional(),
  ahjId: z.string().nullable(),
  name: z.string().min(1, { message: 'Name is required' }),
  criticalUnit: z.boolean(),
  elevatorType: z.string().nullable(),
  installationYear: z.coerce.number().nullable(),
  modernizationYear: z.coerce.number().nullable(),
  numLandings: z.coerce.number().nullable(),
  speedFpm: z.coerce.number().nullable(),
  capacityLbs: z.coerce.number().nullable(),
  controllerModel: z.string().nullable(),
  driveType: z.string().nullable(),
  lastSspiTest: z.string().nullable(),
  numEntrances: z.coerce.number().nullable(),
  oem: z.string().nullable(),
  lastCat1Test: z.string().nullable(),
  lastCat5Test: z.string().nullable(),
});

// The minimal serviced-car shape shared by ticket reads (`{id, name}`) and
// invoice reads (`{id, name, ahjId?, elevatorType?}`). Neither carries the full
// car serializer's fields, so a `carSchema.pick` throws on a real read.
export const carDisplaySchema = z.object({
  id: z.string(),
  name: z.string(),
  ahjId: z.string().optional(),
  // Wire sends the enum-or-blank value space.
  elevatorType: z.string().nullable().optional(),
});

/**
 * One row of `carsKpisByPropertyIdList`, as `usePropertyCarsKpis` reads it.
 *
 * Nothing is overridden — the base is already correct. The nine KPIs are
 * `allow_null` at source (a car with no tickets in the window returns all nine
 * null, which is routine), and an OEM-less car now emits `oem: null` rather than
 * dropping the key (AMP-619).
 */
export const carInPropertySchema = CarsKpisByPropertyIdListResponseItem;

// `.min(0)` is the app's only enrichment — the counts are never negative.
const violationStatisticsSchema = CarsViolationsRetrieveResponse.shape.statistics.extend({
  open: z.number().min(0),
  overdue: z.number().min(0),
  resolved: z.number().min(0),
  total: z.number().min(0),
});

// The schema `violationsFetcher.ts` parses the car-violations read through.
// `affidavitDownloadUrl` / `customResolutionType` are on the wire but never read,
// so they are omitted; Zod strips them and real payloads still parse.
export const violationSchema = CarsViolationsRetrieveResponse.shape.results.element
  .omit({
    affidavitDownloadUrl: true,
    customResolutionType: true,
  })
  .extend({
    // Wire emits CAPITALIZED "Open"/"Overdue"/"Resolved"; the base is a bare
    // string because drf-spectacular cannot infer a method field's value space.
    // The lowercase set belongs to a DIFFERENT serializer (`violationsList`).
    status: z.enum(['Open', 'Overdue', 'Resolved']),
    // Wire value space is `enum | '' | null`; `|| null` (not `??`) collapses all
    // three empties to one `null` so consumers stay on a `T | null` contract.
    responsibility:
      CarsViolationsRetrieveResponse.shape.results.element.shape.responsibility.transform(
        (value) => value || null,
      ),
    resolutionType:
      CarsViolationsRetrieveResponse.shape.results.element.shape.resolutionType.transform(
        (value) => value || null,
      ),
  });

export const violationsResponseSchema = CarsViolationsRetrieveResponse.extend({
  statistics: violationStatisticsSchema,
  results: z.array(violationSchema),
});

/**
 * One row of the car `compliance-documents` action, as `useCarCertificate` reads
 * it. Uses THAT action's base, not `CarsCertificatesListResponseItem`: the
 * certificates base types `expirationDate` non-null, but this action emits null
 * for every inspection-report row and for contracts with no expiration.
 *
 * Nothing is overridden — the base is already correct. These rows carry no `id`;
 * the action emits exactly four keys. `documentType` stays an un-enumerated
 * string so a new backend document type cannot throw.
 */
export const carCertificateSchema = CarsComplianceDocumentsListResponseItem;

export const yearOptions = ((): string[] => {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1950 + 1 }, (_, i) =>
    (1950 + i).toString(),
  ).reverse();

  return years;
})();

export type Violation = z.infer<typeof violationSchema>;

export type CarInProperty = z.infer<typeof carInPropertySchema>;
export type CarDto = z.infer<typeof carDtoSchema>;
